import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Infinity as InfinityIcon, Sparkles, AlertTriangle } from 'lucide-react';
import { useUsage } from '../../hooks/useUsage';
import { UsageResource } from '../../services/subscriptionService';

interface Props {
  resource: UsageResource;
  label?: string;
  upgradeHref?: string;
  compact?: boolean;
  className?: string;
}

const RESOURCE_FALLBACKS: Record<UsageResource, string> = {
  campaigns: 'Campanhas',
  projects: 'Projetos',
  keywords: 'Keywords',
  competitors: 'Concorrentes',
  content_generations: 'Gerações de conteúdo',
  team_members: 'Membros do time',
  automated_workflows: 'Workflows',
  image_generations: 'Imagens'
};

const UsageBadge: React.FC<Props> = ({ resource, label, upgradeHref = '/subscription', compact = false, className = '' }) => {
  const { t } = useTranslation();
  const { summary, isLoading } = useUsage();

  if (isLoading && !summary) {
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-xs text-gray-500 animate-pulse ${className}`}>
        <span className="w-3 h-3 rounded-full bg-gray-300 dark:bg-gray-600" />
        <span>…</span>
      </div>
    );
  }

  const item = summary?.[resource];
  if (!item) return null;

  const displayLabel = label ?? t(`subscription.usage.resources.${resource}`, { defaultValue: RESOURCE_FALLBACKS[resource] });
  const { current, limit, unlimited, remaining, percentage } = item;

  const reached = !unlimited && typeof limit === 'number' && current >= limit;
  const warning = !unlimited && !reached && percentage >= 80;

  const colorClasses = unlimited
    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 border-primary-200 dark:border-primary-800'
    : reached
      ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800'
      : warning
        ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800'
        : 'bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700';

  return (
    <div className={`inline-flex items-center gap-2 px-2.5 sm:px-3 py-1.5 rounded-lg border text-xs font-medium whitespace-nowrap shrink-0 ${colorClasses} ${className}`}>
      {reached && <AlertTriangle className="w-3.5 h-3.5" />}
      {unlimited ? (
        <span className="inline-flex items-center gap-1">
          <InfinityIcon className="w-3.5 h-3.5" />
          {!compact && <span>{displayLabel} — {t('subscription.usage.unlimited', { defaultValue: 'Ilimitado' })}</span>}
          {compact && <span>{current}</span>}
        </span>
      ) : (
        <span>
          {/* No celular só "0/1" (o nome inteiro empurrava os botões do cabeçalho) */}
          {compact ? `${current}/${limit}` : (
            <>
              <span className="sm:hidden" title={displayLabel}>{current}/{limit}</span>
              <span className="hidden sm:inline">{`${current}/${limit} ${displayLabel.toLowerCase()}`}</span>
            </>
          )}
          {!compact && typeof remaining === 'number' && remaining > 0 && remaining <= 3 && (
            <span className="hidden sm:inline ml-1 opacity-80">({t('subscription.usage.remaining', { remaining, defaultValue: `${remaining} restantes` })})</span>
          )}
        </span>
      )}
      {!unlimited && (reached || warning) && (
        <Link
          to={upgradeHref}
          className="inline-flex items-center gap-1 ml-1 px-2 py-0.5 rounded-md bg-white/60 dark:bg-black/20 hover:bg-white dark:hover:bg-black/30 text-[11px] font-semibold transition-colors"
        >
          <Sparkles className="w-3 h-3" />
          {t('subscription.limitExceeded.upgrade', { defaultValue: 'Fazer Upgrade' })}
        </Link>
      )}
    </div>
  );
};

export default UsageBadge;
